import { createContext, FC, ReactNode, useCallback, useMemo } from 'react';

import { BookingInfo } from 'src/api/bookingsView';
import { useConfirmationDialog } from 'src/components/ConfirmationDialog/useConfirmationDialog';
import { useRequiredContext } from 'src/hooks/useRequiredContext';
import { fetchBookings, unbookBooking } from 'src/store/bookings/services';
import { bookingsActions } from 'src/store/bookings/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface MyBookingsContextValue {
  loadMore: (startIndex: number, stopIndex: number) => Promise<void>;
  unbookDialog: ReactNode;
  unbook: (booking: BookingInfo) => Promise<void>;
}

export const MyBookingsContext = createContext<
  MyBookingsContextValue | undefined
>(undefined);
MyBookingsContext.displayName = 'MyBookingsContext';

interface MyBookingsProviderProps {
  children?: ReactNode;
}

export const MyBookingsProvider: FC<MyBookingsProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const loadMore = useCallback(async () => {
    await dispatch(fetchBookings(false));
  }, [dispatch]);

  const {
    dialog: unbookDialog,
    open: openUnbookDialog,
    confirmation,
  } = useConfirmationDialog({
    title: 'Отменить бронирование?',
    okLabel: 'Отменить',
    cancelLabel: 'Назад',
    text: 'После отмены бронирования это действие нельзя будет отменить. Чтобы создать новое бронирование, тебе нужно будет снова пройти весь процесс.',
  });

  const unbook = useCallback(
    async (booking: BookingInfo) => {
      dispatch(
        bookingsActions.setUnbookingState({
          id: booking.bookingId,
          value: { key: 'approve' },
        }),
      );
      openUnbookDialog();
      const confirmed = await confirmation();
      if (!confirmed) {
        dispatch(
          bookingsActions.setUnbookingState({
            id: booking.bookingId,
            value: { key: 'canceled' },
          }),
        );
        return;
      }
      await dispatch(unbookBooking(booking));
    },
    [confirmation, dispatch, openUnbookDialog],
  );

  const contextValue = useMemo((): MyBookingsContextValue => {
    return {
      loadMore,
      unbookDialog,
      unbook,
    };
  }, [loadMore, unbookDialog, unbook]);

  return (
    <MyBookingsContext.Provider value={contextValue}>
      {children}
    </MyBookingsContext.Provider>
  );
};

export const useMyBookings = (): MyBookingsContextValue =>
  useRequiredContext(MyBookingsContext);
